import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';

import { AnalyticsStateService } from './analytics-state.service';
import { GroupPracticeStateService } from './group-practice-state.service';
import { HomeStateService } from './home-state.service';
import { NotificationStateService } from './notification.state.service';
import { UserFamilyStateService } from './user-family-state.service';
import { UserProfileStateService } from './user-profile.state.service';
import { environment } from '../../../environments/environment';
import { LocalStorageKeys, NotificationType } from '../../shared/helpers/app.constants';

const NotificationRecordTypes = {
  Quiz: 'Quiz',
  PracticeQuestion: 'PracticeQuestion',
  UserFamily: 'UserFamily',
  GroupPractice: 'GroupPractice',
}

interface IRecordUpdatedMessageModel {
  recordType: string;
  action: string;
  recordId: string;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationHubStateService {
  baseUrl = environment.apiBaseUrl;
  public signalRConnectionId: string | null = null;
  public readonly hubConnection: signalR.HubConnection;
  public readonly connectionStateSubject = new BehaviorSubject<boolean>(false);
  public readonly isConnected$ = this.connectionStateSubject.asObservable();

  public readonly revealSubject = new Subject<void>();
  public readonly reveal$ = this.revealSubject.asObservable();


  public get isConnected(): boolean {
    return this.hubConnection.state === signalR.HubConnectionState.Connected;
  }

  constructor(
    private readonly notificationStateService: NotificationStateService,
    private readonly homeStateService: HomeStateService,
    private readonly analyticsStateService: AnalyticsStateService,
    private readonly userProfileStateService: UserProfileStateService,
    private readonly userFamilyStateService: UserFamilyStateService,
    private readonly groupPracticeStateService: GroupPracticeStateService,
  ) {
    const options: signalR.IHttpConnectionOptions = {
      accessTokenFactory: () =>
        localStorage.getItem(LocalStorageKeys.AuthToken)
        || localStorage.getItem(LocalStorageKeys.GuestToken)
        || ''
    };

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.baseUrl}notification-hub`, options)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => 5000
      })
      .build();
    this.registerServerEvents();
    this.registerConnectionEvents();
  }

  public startConnection(): void {

    if (this.hubConnection.state === signalR.HubConnectionState.Connected || this.hubConnection.state === signalR.HubConnectionState.Connecting) {
      return;
    }

    this.hubConnection
      .start()
      .then(() => {
        console.log('Connection started');
        this.connectionStateSubject.next(true);
      })
      .catch((err: string) => {
        console.error('Error while starting connection: ' + err);
        if (this.connectionStateSubject.value) {
          this.connectionStateSubject.next(false);
        }
        setTimeout(() => {
          this.startConnection();
        }, 5000);
      });
  }

  registerServerEvents(): void {
    this.hubConnection.on('TestConnection', (connectionId: string) => {
      this.signalRConnectionId = connectionId;
      console.log('✅ SignalR test message from backend:');
    });

    this.hubConnection.on(NotificationType.Notification, () => {
      this.notificationStateService.updateCountState();
    });

    this.hubConnection.on(NotificationType.RecordUpdated, (model: IRecordUpdatedMessageModel) => {
      switch (model.recordType) {
        case NotificationRecordTypes.Quiz:
          this.analyticsStateService.refreshState();
          this.homeStateService.updateStreakOverviewState();
          this.userProfileStateService.updateRecentExamState();
          break;
        case NotificationRecordTypes.PracticeQuestion:
          this.analyticsStateService.refreshState();
          this.homeStateService.updateStreakOverviewState();
          break;
        case NotificationRecordTypes.UserFamily:
          this.userFamilyStateService.refreshFamily();
          if (model.action === 'Exited' || model.action === 'Revoked' || model.action === 'Accepted') {
            this.userProfileStateService.updateToken().subscribe({
              error: (err) => console.error('Error while updating token: ', err),
            });
          }
          break;
      }
    });

    this.hubConnection.on(NotificationType.Disconnected, () => {
      console.log('Disconnected called.');
    });

    this.hubConnection.on(NotificationType.GroupPractice, (model: IRecordUpdatedMessageModel) => {
      if (model.recordId !== this.groupPracticeStateService.getStateValue().groupId) {
        return;
      }

      switch (model.action) {
        case 'MemberJoined':
            this.groupPracticeStateService.loadDetail(model.recordId).subscribe();
          break;
        case 'Start':
          this.groupPracticeStateService.applySessionStarted();
          break;
        case 'Next':
          if (!this.groupPracticeStateService.getStateValue().isHost && !this.groupPracticeStateService.getStateValue().hasLeaved) {
            this.groupPracticeStateService.nextQuestion(model.recordId).subscribe();
          }
          break;
        case 'Reveal':
          this.revealSubject.next();
          break;
        case 'Complete':
          this.groupPracticeStateService.applySessionCompleted();
          break;
        case 'Leave':
          this.groupPracticeStateService.loadDetail(model.recordId).subscribe();
          this.groupPracticeStateService.refreshSubmissionCount().subscribe();
          break;
        case 'AnswerSubmitted':
          this.groupPracticeStateService.refreshSubmissionCount().subscribe();
          break;
        case 'AnswerSkipped':
          this.groupPracticeStateService.refreshSubmissionCount().subscribe();
          break;
      }
    });
  }

  public restartConnection(): void {
    if (this.hubConnection) {
      this.hubConnection.stop().then(() => {
        this.hubConnection.start()
          .then(() => console.log('✅ SignalR reconnected with new token'))
          .catch(err => console.error('❌ Reconnect error:', err));
      });
    }
  }

  public stopConnection(): void {
    this.hubConnection
      .stop()
      .then(() => console.log('Connection stopped'))
      .catch((err: string) => console.error('Error while stopping connection: ' + err));
  }

  private registerConnectionEvents(): void {
    this.hubConnection.onreconnecting(() => {
      this.connectionStateSubject.next(false);
    });

    this.hubConnection.onreconnected(() => {
      this.connectionStateSubject.next(true);
    });

    this.hubConnection.onclose(() => {
      this.connectionStateSubject.next(false);
    });
  }
}
